import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";

// Proofix Logo Component
const ProofixLogo = ({ className = "h-8" }) => (
  <svg 
    className={className} 
    viewBox="0 0 200 50" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Shield with checkmark */}
    <defs>
      <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1DA1F2" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
    </defs>
    <path 
      d="M8 8C8 8 20 4 20 4C20 4 32 8 32 8C32 8 32 24 32 24C32 32 20 40 20 40C20 40 8 32 8 32C8 32 8 8 8 8Z" 
      fill="url(#shieldGradient)"
    />
    {/* Checkmark */}
    <path 
      d="M14 20L18 24L26 14" 
      stroke="#1e293b" 
      strokeWidth="3" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    {/* Plus sign */}
    <circle cx="30" cy="14" r="6" fill="#1e293b" />
    <path d="M30 11V17M27 14H33" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    
    {/* Text: Proofix */}
    <text x="44" y="30" fontFamily="Outfit, sans-serif" fontWeight="600" fontSize="22" fill="#1e293b">
      Proof
    </text>
    <text x="108" y="30" fontFamily="Outfit, sans-serif" fontWeight="600" fontSize="22" fill="#1DA1F2">
      ix
    </text>
  </svg>
);

// Alternative: Image-based logo
const ProofixLogoImage = ({ className = "h-8" }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <img 
      src="https://customer-assets.emergentagent.com/job_checklab-dba/artifacts/fiiictma_ChatGPT%20Image%2026%20mrt%202026%2C%2011_48_56.png"
      alt="Proofix"
      className="h-8 w-auto"
    />
  </div>
);

const Header = () => {
  const location = useLocation();
  const [isPartner, setIsPartner] = useState(false);
  
  // Check if user is logged in as partner
  useEffect(() => {
    const checkPartnerStatus = () => {
      const token = localStorage.getItem("partner_token");
      setIsPartner(!!token);
    };
    
    checkPartnerStatus();
    // Listen for storage changes (login/logout in other tabs)
    window.addEventListener("storage", checkPartnerStatus);
    
    return () => window.removeEventListener("storage", checkPartnerStatus);
  }, []);
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <motion.header
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass-header sticky top-0 z-50 px-6 md:px-16 lg:px-24 py-4"
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-3"
          data-testid="logo-link"
        >
          {/* Shield icon */}
          <div className="relative">
            <svg 
              className="h-9 w-auto" 
              viewBox="0 0 40 48" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="headerShieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1DA1F2" />
                  <stop offset="100%" stopColor="#2563EB" />
                </linearGradient>
              </defs>
              <path 
                d="M4 10C4 10 20 4 20 4C20 4 36 10 36 10V28C36 36 20 44 20 44C20 44 4 36 4 36V10Z" 
                fill="url(#headerShieldGradient)"
              />
              <path 
                d="M12 24L18 30L28 18" 
                stroke="#1e293b" 
                strokeWidth="3.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                fill="none"
              />
              <circle cx="32" cy="12" r="7" fill="#1e293b" />
              <path d="M32 8V16M28 12H36" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <span className="font-outfit text-xl font-semibold tracking-tight">
            <span className="text-zinc-900">Proof</span>
            <span className="text-[#1DA1F2]">ix</span>
          </span>
        </Link>

        {/* Navigation */}
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/dba-check"
            className={`text-sm font-medium transition-colors ${
              isActive("/dba-check") || location.pathname.startsWith("/dba-check")
                ? "text-blue-600"
                : "text-zinc-600 hover:text-zinc-900"
            }`}
            data-testid="nav-dba-check"
          >
            DBA Check
          </Link>
          
          {/* Partner button - changes based on login status */}
          <Link
            to={isPartner ? "/partner-dashboard" : "/partner"}
            data-testid="nav-partner"
            className="hidden sm:flex items-center gap-2"
          >
            {isPartner ? (
              <button className="rounded-full px-4 py-2 text-sm font-medium bg-gradient-to-r from-emerald-500 to-blue-500 text-white hover:from-emerald-600 hover:to-blue-600 transition-all hover:-translate-y-0.5 shadow-sm">
                Partner portaal
              </button>
            ) : (
              <>
                <button className="rounded-full px-4 py-2 text-sm font-medium border-2 border-blue-500 text-blue-600 hover:bg-blue-50 transition-all hover:-translate-y-0.5">
                  Partner worden
                </button>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full hidden md:inline">
                  Tot €500
                </span>
              </>
            )}
          </Link>
          
          <Link
            to="/dba-check"
            data-testid="nav-cta"
          >
            <button className="rounded-full px-5 py-2 text-sm font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/25">
              Start Check
            </button>
          </Link>
        </div>
      </nav>
    </motion.header>
  );
};

export default Header;
